/**
 * Tilannekatsaus — project-scoped status overview.
 *
 * Nine tabs, one per canonical task title. The grouping mirrors the three
 * include-flag groups in the init worker:
 *   - PP tabs (4): procurement-level tasks.
 *   - WS tabs (5): section/space-level tasks.
 *
 * Each tab lists the matching tasks for the selected project with status,
 * responsible user, deadline, and contextual PP/WS fields. Tabs are filtered
 * by exact (verbatim) title — see useOverviewTasks.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { formatResponsible } from "@/lib/responsibleUser";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronsUpDownIcon,
  ProcurementPackageIcon,
  WorkSectionIcon,
  CongridIcon,
  TypeIcon,
  SpaceIcon,
  PlotIcon,
  UserIcon,
  StatusIcon,
  CalendarIcon,
  TimeIcon,
  QualityMatrixIcon,
  SearchIcon,
  CloseIcon,
  type LucideIcon,
} from "@/lib/icons";
import {
  TASK_STATUS_COLORS,
  TASK_ROW_BG_BY_STATUS,
  TASK_ROW_HOVER_BG_BY_STATUS,
} from "@/lib/taskStatusMeta";
import { useProjects } from "@/hooks/useProjects";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  useOverviewTasks,
  type OverviewTask,
} from "@/hooks/useOverviewTasks";
import { useAuth } from "@/contexts/AuthContext";
import { useUsers } from "@/hooks/useUsers";
import { useTaskMutations } from "@/hooks/useTasks";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { QualityMatrix } from "@/components/overview/QualityMatrix";
import { TaskStatus, type TaskExtended } from "@/types";

const PP_TABS = [
  "Suunnitelmiin perehtyminen",
  "Aliurakkasopimukseen perehtyminen",
  "Aloitusedellytykset",
  "Taloudellinen loppuselvitys",
];
const WS_TABS = [
  "Mestan vastaanotto",
  "Malliasennustarkastus",
  "Osakohteen tarkastus",
  "Työvaiheenvastaanotto",
  "Liitedokumentit",
];
const ALL_TABS = [...PP_TABS, ...WS_TABS];

const DASH = "–";

type SortDir = "asc" | "desc";

interface Column {
  key: string;
  label: string;
  icon: LucideIcon;
  type: "string" | "date" | "number";
  /** Sort value for string columns. */
  getString?: (task: OverviewTask) => string;
  /** Sort value (epoch ms) for date columns; null when absent. */
  getDate?: (task: OverviewTask) => number | null;
  /** Sort value for numeric columns; null sorts last. */
  getNumber?: (task: OverviewTask) => number | null;
  render: (task: OverviewTask) => React.ReactNode;
}

function responsibleName(task: OverviewTask): string {
  return formatResponsible(task.responsibleUser) || DASH;
}

/**
 * Share of completed subtasks (0–100) for sorting the status column.
 * Returns null when the task has no countable subtasks so those rows sort
 * last, matching the date columns' null handling.
 */
function completionPercent(task: OverviewTask): number | null {
  const p = task.subtaskProgress;
  if (!p || p.total === 0) return null;
  return (p.completed / p.total) * 100;
}

function locationPath(task: OverviewTask): string {
  return task.location?.hierarchyPath?.join(" › ") ?? DASH;
}

function plotName(task: OverviewTask): string {
  return task.plot?.name ?? DASH;
}

/** Combine a code and name into "code - name", falling back to whichever exists. */
function codeNameLabel(
  code: string | null | undefined,
  name: string | null | undefined,
): string {
  const c = code ?? "";
  const n = name ?? "";
  if (c && n) return `${c} - ${n}`;
  return c || n || "";
}

function parseDate(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return DASH;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? DASH : d.toLocaleDateString("fi-FI");
}

/**
 * A task is "myöhässä" (late) when its deadline is strictly before today and
 * the task is not yet COMPLETED. Tasks with no deadline are never late; the
 * time-of-day is ignored so a task due today is not yet late. Congrid terminal
 * states other than COMPLETED (rejected, not-applicable, …) still count as
 * late by design.
 */
function isTaskLate(task: OverviewTask): boolean {
  const due = parseDate(task.dueDate);
  if (due == null || task.status === TaskStatus.COMPLETED) return false;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return due < startOfToday.getTime();
}

/**
 * Stacked subtask-progress bar (completed → in-progress → problem over a
 * pending track), mirroring the breakdown on Hankkeet's HeadsectionColumn.
 * The exact per-status counts ride along in the title/aria tooltip. Parent
 * tasks derive their status from their children, so a task with no countable
 * subtasks has no status and renders a dash.
 */
const StatusBar = ({ task }: { task: OverviewTask }) => {
  const p = task.subtaskProgress;
  if (!p || p.total === 0) {
    return <span className="text-muted-foreground">{DASH}</span>;
  }
  const completed = (p.completed / p.total) * 100;
  const inProgress = (p.inProgress / p.total) * 100;
  const problem = (p.problem / p.total) * 100;
  const summary = `${p.completed} valmis · ${p.inProgress} työn alla · ${p.problem} ongelma · ${p.pending} aloittamatta`;
  return (
    <div
      className={cn(
        "flex h-2 w-full min-w-[80px] overflow-hidden rounded-full",
        TASK_STATUS_COLORS[TaskStatus.PENDING].track,
      )}
      title={summary}
      aria-label={summary}
    >
      {completed > 0 && (
        <div
          className={cn("h-full", TASK_STATUS_COLORS[TaskStatus.COMPLETED].bar)}
          style={{ width: `${completed}%` }}
        />
      )}
      {inProgress > 0 && (
        <div
          className={cn(
            "h-full",
            TASK_STATUS_COLORS[TaskStatus.IN_PROGRESS].bar,
          )}
          style={{ width: `${inProgress}%` }}
        />
      )}
      {problem > 0 && (
        <div
          className={cn("h-full", TASK_STATUS_COLORS[TaskStatus.PROBLEM].bar)}
          style={{ width: `${problem}%` }}
        />
      )}
    </div>
  );
};

const RESPONSIBLE_COL: Column = {
  key: "responsible",
  label: "Vastuuhenkilö",
  icon: UserIcon,
  type: "string",
  getString: responsibleName,
  render: (t) => responsibleName(t),
};

const STATUS_COL: Column = {
  key: "status",
  label: "Tila",
  icon: StatusIcon,
  type: "number",
  getNumber: completionPercent,
  render: (t) => <StatusBar task={t} />,
};

const DUE_COL: Column = {
  key: "dueDate",
  label: "Määräaika",
  icon: CalendarIcon,
  type: "date",
  getDate: (t) => parseDate(t.dueDate),
  render: (t) => {
    const late = isTaskLate(t);
    return (
      <span className="flex items-center gap-2">
        <span>{formatDate(t.dueDate)}</span>
        {late && (
          <span
            role="img"
            aria-label="Myöhässä"
            title="Myöhässä"
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500 ring-2 ring-red-500/25"
          />
        )}
      </span>
    );
  },
};

const UPDATED_COL: Column = {
  key: "updatedAt",
  label: "Tila päivitetty",
  icon: TimeIcon,
  type: "date",
  getDate: (t) => parseDate(t.updatedAt),
  render: (t) => formatDate(t.updatedAt),
};

const PP_COLUMNS: Column[] = [
  {
    key: "procurementPackage",
    label: "Hankintapaketti",
    icon: ProcurementPackageIcon,
    type: "string",
    getString: (t) =>
      codeNameLabel(t.procurementPackage?.code, t.procurementPackage?.name),
    render: (t) =>
      codeNameLabel(t.procurementPackage?.code, t.procurementPackage?.name) ||
      DASH,
  },
  {
    key: "plot",
    label: "Tontti",
    icon: PlotIcon,
    type: "string",
    getString: plotName,
    render: (t) => plotName(t),
  },
  RESPONSIBLE_COL,
  STATUS_COL,
  DUE_COL,
  UPDATED_COL,
];

const WS_COLUMNS: Column[] = [
  {
    key: "workSection",
    label: "Työvaihe",
    icon: WorkSectionIcon,
    type: "string",
    getString: (t) => codeNameLabel(t.section?.code, t.section?.name),
    render: (t) => codeNameLabel(t.section?.code, t.section?.name) || DASH,
  },
  {
    key: "plot",
    label: "Tontti",
    icon: PlotIcon,
    type: "string",
    getString: plotName,
    render: (t) => plotName(t),
  },
  {
    key: "location",
    label: "Sijainti",
    icon: SpaceIcon,
    type: "string",
    getString: locationPath,
    render: (t) => locationPath(t),
  },
  RESPONSIBLE_COL,
  STATUS_COL,
  DUE_COL,
  UPDATED_COL,
];

function OverviewTabContent({
  projectId,
  title,
  onTaskClick,
  registerRefetch,
}: {
  projectId: string;
  title: string;
  onTaskClick: (task: OverviewTask) => void;
  /**
   * Hands the active query's refetch up to the parent so the dialog (a
   * sibling of this component) can refresh the list after subtask edits —
   * keeping the row's status colour and progress bar in sync.
   */
  registerRefetch: (refetch: (() => void) | null) => void;
}) {
  const { tasks, loading, refetch } = useOverviewTasks(projectId, title);
  const columns = PP_TABS.includes(title) ? PP_COLUMNS : WS_COLUMNS;

  useEffect(() => {
    registerRefetch(() => {
      void refetch();
    });
    return () => registerRefetch(null);
  }, [registerRefetch, refetch]);

  const [sortKey, setSortKey] = useState(columns[0].key);
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const [search, setSearch] = useState("");
  const [matchIndex, setMatchIndex] = useState(0);
  const debouncedSearch = useDebouncedValue(search, 150);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedTasks = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey) ?? columns[0];
    const dir = sortDir === "asc" ? 1 : -1;
    return [...tasks].sort((a, b) => {
      if (col.type === "date" || col.type === "number") {
        const get = col.type === "date" ? col.getDate : col.getNumber;
        const ta = get?.(a) ?? null;
        const tb = get?.(b) ?? null;
        // Nulls sort last regardless of direction.
        if (ta == null && tb == null) return 0;
        if (ta == null) return 1;
        if (tb == null) return -1;
        return (ta - tb) * dir;
      }
      const sa = col.getString?.(a) ?? "";
      const sb = col.getString?.(b) ?? "";
      return sa.localeCompare(sb, "fi") * dir;
    });
  }, [tasks, sortKey, sortDir, columns]);

  /**
   * Row ids matching the search, in the order they are rendered. Matching
   * reuses each column's own `getString`, so what is searched is exactly what
   * the row shows — no separate notion of searchable text to drift.
   */
  const matches = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    if (!needle) return [] as string[];
    return sortedTasks
      .filter((task) =>
        columns.some((col) =>
          (col.getString?.(task) ?? "").toLowerCase().includes(needle),
        ),
      )
      .map((task) => task.id);
  }, [sortedTasks, columns, debouncedSearch]);

  useEffect(() => {
    setMatchIndex(0);
  }, [debouncedSearch, sortKey, sortDir]);

  const currentMatchId = matches[matchIndex] ?? null;

  useEffect(() => {
    if (!currentMatchId) return;
    rowRefs.current
      .get(currentMatchId)
      ?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [currentMatchId]);

  const stepMatch = (delta: number) => {
    if (matches.length === 0) return;
    setMatchIndex((i) => (i + delta + matches.length) % matches.length);
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        Ei tehtäviä tällä välilehdellä.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex shrink-0 items-center gap-2">
        <div className="relative w-72 max-w-full">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                stepMatch(e.shiftKey ? -1 : 1);
              }
              if (e.key === "Escape") setSearch("");
            }}
            placeholder="Hae riveistä, esim. 5058"
            className="h-8 pl-8 pr-8 text-xs"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Tyhjennä haku"
            >
              <CloseIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {debouncedSearch.trim() && (
          <span className="text-xs text-muted-foreground">
            {matches.length === 0
              ? "Ei osumia"
              : `${matchIndex + 1}/${matches.length} · Enter seuraava, Vaihto+Enter edellinen`}
          </span>
        )}
      </div>

      {/* The wrapper owns both scroll axes, so it — not the page — is the
          scrollport the sticky column headers anchor to, and its horizontal
          scrollbar stays on screen instead of sitting below the last row. */}
      <Table wrapperClassName="min-h-0 flex-1 rounded-md border">
      <TableHeader>
        <TableRow>
          {columns.map((col) => (
            <TableHead
              key={col.key}
              className={cn(
                "h-9 cursor-pointer select-none border-r last:border-r-0",
                // The inset shadow stands in for the header row's bottom
                // border, which a sticky cell would otherwise scroll past.
                "sticky top-0 z-10 bg-background",
                "shadow-[inset_0_-1px_0_hsl(var(--border))]",
              )}
              onClick={() => handleSort(col.key)}
            >
              <span className="flex items-center gap-1.5">
                <col.icon className="h-3.5 w-3.5 text-muted-foreground" />
                {col.label}
                {sortKey === col.key ? (
                  sortDir === "asc" ? (
                    <ChevronUpIcon className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDownIcon className="h-3.5 w-3.5" />
                  )
                ) : (
                  <ChevronsUpDownIcon className="h-3.5 w-3.5 opacity-30" />
                )}
              </span>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedTasks.map((task) => (
          <TableRow
            key={task.id}
            ref={(el) => {
              if (el) rowRefs.current.set(task.id, el);
              else rowRefs.current.delete(task.id);
            }}
            onClick={() => onTaskClick(task)}
            className={cn(
              "cursor-pointer",
              TASK_ROW_BG_BY_STATUS[task.status],
              TASK_ROW_HOVER_BG_BY_STATUS[task.status],
              task.id === currentMatchId &&
                "outline outline-2 -outline-offset-2 outline-primary",
            )}
          >
            {columns.map((col) => (
              <TableCell
                key={col.key}
                className="py-1.5 border-r last:border-r-0"
              >
                {col.render(task)}
              </TableCell>
            ))}
          </TableRow>
        ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function Tilannekatsaus() {
  const { projects } = useProjects();
  const { user } = useAuth();
  const { users } = useUsers();
  const { updateTaskStatus } = useTaskMutations();
  const [selectedProjectId, setSelectedProjectId] = useLocalStorage<
    string | null
  >("tilannekatsaus.projectId", null);
  const [activeTab, setActiveTab] = useLocalStorage<string>(
    "tilannekatsaus.activeTab",
    PP_TABS[0],
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskExtended | null>(null);
  const [matrixOpen, setMatrixOpen] = useState(false);

  // The overview query lives inside OverviewTabContent, but the dialog is a
  // sibling here. OverviewTabContent registers its refetch so subtask edits
  // can refresh the row's status and subtaskProgress together.
  const refetchOverviewRef = useRef<(() => void) | null>(null);
  const registerRefetch = useCallback((refetch: (() => void) | null) => {
    refetchOverviewRef.current = refetch;
  }, []);

  const currentUserId = useMemo(() => {
    if (!user?.id) return "";
    return users.find((u) => u.azureObjectId === user.id)?.id || "";
  }, [user, users]);

  // The overview list fetches only a subset of task fields; TaskDialog
  // lazy-loads the full task by id (GET_TASK_DETAIL, cache-and-network), so a
  // minimal snapshot is all that's needed to open it. See useOverviewTasks.
  const openTaskDialog = useCallback((task: OverviewTask) => {
    setSelectedTask(task as unknown as TaskExtended);
    setDialogOpen(true);
  }, []);

  // The matrix carries only task ids. TaskDialog resolves the rest through
  // GET_TASK_DETAIL, so an id-only snapshot is enough to open it.
  const openTaskDialogById = useCallback((taskId: string) => {
    setSelectedTask({ id: taskId } as unknown as TaskExtended);
    setDialogOpen(true);
  }, []);

  const handleUpdateStatus = async (taskId: string, status: TaskStatus) => {
    if (selectedTask?.id === taskId && selectedTask.version != null) {
      try {
        await updateTaskStatus(taskId, status, selectedTask.version);
      } catch (error) {
        console.error("Failed to update task status:", error);
      }
    }
    setDialogOpen(false);
  };

  return (
    // The page fills the viewport rather than growing, so the table region
    // below owns the vertical scroll. That keeps the frozen top region, the
    // scroll rail and the sticky column headers all on screen. The height
    // subtracts AppLayout's p-6 gutters; -mt-6 below reclaims the top one.
    <div className="flex h-[calc(100dvh-3rem)] flex-col">
      {/* Frozen top region: the header bar and the tab selectors stay pinned
          at the top while the task table scrolls beneath them. */}
      <div className="sticky top-0 z-20 -mt-6 shrink-0 bg-background">
        <div className="-mx-6 flex flex-wrap items-center gap-4 border-b bg-background px-4 py-3">
          <h1 className="text-lg font-semibold">Tilannekatsaus</h1>
          <Combobox
            className="w-72"
            value={selectedProjectId}
            onChange={setSelectedProjectId}
            options={projects.map((project) => ({
              value: project.id,
              label: project.name,
            }))}
            placeholder="Valitse hanke"
            searchPlaceholder="Hae hanketta..."
          />
          {selectedProjectId && (
            <button
              type="button"
              onClick={() => setMatrixOpen((open) => !open)}
              aria-pressed={matrixOpen}
              className={cn(
                "flex h-9 items-center gap-1.5 rounded-md px-3 text-xs transition-colors duration-150",
                "active:scale-[0.97]",
                matrixOpen
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "bg-muted text-foreground hover:bg-muted/50",
              )}
            >
              <QualityMatrixIcon className="h-3.5 w-3.5" />
              Laatumatriisi
            </button>
          )}
        </div>

        {selectedProjectId && !matrixOpen && (
          <div className="pt-6">
            {/* PP tasks (first four) and WS tasks (last five) each get their own
                titled, wrapping row so the two groups never share a line. */}
            {[
              {
                tabs: PP_TABS,
                title: "Hankintapakettitehtävät",
                icon: ProcurementPackageIcon,
              },
              {
                tabs: WS_TABS,
                title: "Congrid tehtävät",
                icon: CongridIcon,
              },
            ].map((group, groupIndex) => {
              const TabIcon = group.icon;
              return (
                <div key={groupIndex} className="mb-2 space-y-2">
                  <h2 className="flex items-center gap-2 text-xs uppercase tracking-wide font-medium text-muted-foreground">
                    <TabIcon className="h-4 w-4 shrink-0" />
                    {group.title}
                  </h2>
                  <div className="flex gap-2">
                    {group.tabs.map((title) => {
                      const selected = activeTab === title;
                      return (
                        <button
                          key={title}
                          type="button"
                          onClick={() => setActiveTab(title)}
                          aria-pressed={selected}
                          className={cn(
                            "flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-xs transition-colors duration-150",
                            "active:scale-[0.97]",
                            selected
                              ? "bg-primary text-primary-foreground shadow-sm"
                              : "bg-muted text-foreground hover:bg-muted/50",
                          )}
                        >
                          <span className="truncate">{title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!selectedProjectId ? (
        <div className="py-16 text-center text-muted-foreground">
          Valitse ensin hanke.
        </div>
      ) : (
        <div className="mt-4 min-h-0 flex-1">
          {matrixOpen ? (
            <QualityMatrix
              projectId={selectedProjectId}
              onItemClick={openTaskDialogById}
              registerRefetch={registerRefetch}
            />
          ) : (
            <OverviewTabContent
              projectId={selectedProjectId}
              title={activeTab}
              onTaskClick={openTaskDialog}
              registerRefetch={registerRefetch}
            />
          )}
        </div>
      )}

      <TaskDialog
        task={selectedTask}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onUpdateStatus={handleUpdateStatus}
        currentUserId={currentUserId}
        onTaskUpdated={setSelectedTask}
        onSubtasksChanged={() => refetchOverviewRef.current?.()}
      />
    </div>
  );
}
